// src/utils/formatters.js
// Centralized formatting utilities for the Elvis Admin Console

/**
 * Format a date string to a localized display format
 * @param {string|Date} dateString - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'Never';

    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...options,
    };

    return new Date(dateString).toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format a date string to a short display format (no time)
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
    if (!dateString) return 'Never';

    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

/**
 * Format a date string to relative time (e.g., "2 hours ago")
 * @param {string|Date} dateString - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    // Handle future dates
    if (diffMs < 0) {
        const futureDiffMs = Math.abs(diffMs);
        const futureDiffMins = Math.floor(futureDiffMs / 60000);
        const futureDiffHours = Math.floor(futureDiffMs / 3600000);
        const futureDiffDays = Math.floor(futureDiffMs / 86400000);

        if (futureDiffMins < 60) return `in ${futureDiffMins} minutes`;
        if (futureDiffHours < 24) return `in ${futureDiffHours} hours`;
        if (futureDiffDays < 7) return `in ${futureDiffDays} days`;
        return formatDate(dateString);
    }

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;

    return formatDate(dateString);
};

/**
 * Format a duration between two dates
 * @param {string|Date} startedAt - Start time
 * @param {string|Date} completedAt - End time (optional, defaults to now)
 * @returns {string} Formatted duration string
 */
export const formatDuration = (startedAt, completedAt) => {
    if (!startedAt) return '-';

    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const duration = Math.round((end - start) / 1000);

    if (duration < 0) return '-';
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.round(duration / 60)}m`;
    if (duration < 86400) return `${Math.round(duration / 3600)}h`;
    return `${Math.round(duration / 86400)}d`;
};

/**
 * Format a duration in milliseconds to a readable string
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration string
 */
export const formatDurationMs = (ms) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${Math.round(ms / 3600000)}h`;
};

/**
 * Format bytes to human-readable file size
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes) return '-';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Format a number with thousand separators
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US');
};

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: EUR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return '-';

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

/**
 * Format a percentage value
 * @param {number} value - Value to format (0-100 or 0-1)
 * @param {number} decimals - Number of decimal places
 * @param {boolean} isDecimal - Whether value is in decimal form (0-1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1, isDecimal = false) => {
    if (value === null || value === undefined) return '-';

    const percentage = isDecimal ? value * 100 : value;
    return `${percentage.toFixed(decimals)}%`;
};

/**
 * Truncate a string with ellipsis
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export const truncateString = (str, maxLength = 50) => {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
};

/**
 * Format a status string for display (replace underscores/dashes with spaces)
 * @param {string} status - Status string
 * @returns {string} Formatted status string
 */
export const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').replace(/-/g, ' ');
};

/**
 * Capitalize the first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Format a cron expression to human-readable format
 * @param {string} cronExpression - Cron expression
 * @returns {string} Human-readable schedule description
 */
export const formatCronExpression = (cronExpression) => {
    if (!cronExpression) return '-';

    // Common cron patterns
    const patterns = {
        '* * * * *': 'Every minute',
        '*/5 * * * *': 'Every 5 minutes',
        '*/10 * * * *': 'Every 10 minutes',
        '*/15 * * * *': 'Every 15 minutes',
        '*/30 * * * *': 'Every 30 minutes',
        '0 * * * *': 'Every hour',
        '0 */2 * * *': 'Every 2 hours',
        '0 */4 * * *': 'Every 4 hours',
        '0 */6 * * *': 'Every 6 hours',
        '0 */12 * * *': 'Every 12 hours',
        '0 0 * * *': 'Daily at midnight',
        '0 6 * * *': 'Daily at 6:00 AM',
        '0 12 * * *': 'Daily at noon',
        '0 18 * * *': 'Daily at 6:00 PM',
        '0 0 * * 0': 'Weekly on Sunday',
        '0 0 * * 1': 'Weekly on Monday',
        '0 0 1 * *': 'Monthly on the 1st',
    };

    return patterns[cronExpression] || cronExpression;
};

/**
 * Format an ISO date string to time only
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (dateString) => {
    if (!dateString) return '-';

    return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });
};
